import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { EditorContextKeys } from 'vscode/vscode/vs/editor/common/editorContextKeys';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { TextFileEditor } from 'vscode/vscode/vs/workbench/contrib/files/browser/editors/textFileEditor';
import { MultiDiffEditor } from './multiDiffEditor.js';
import { MultiDiffEditorInput } from 'vscode/vscode/vs/workbench/contrib/multiDiffEditor/browser/multiDiffEditorInput';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

const _moduleId = "vs/workbench/contrib/multiDiffEditor/browser/actions";
class GoToFileAction extends Action2 {
    constructor() {
        super({
            id: 'multiDiffEditor.goToFile',
            title: ( localize2WithPath(_moduleId, 0, 'Open File')),
            icon: Codicon.goToFile,
            precondition: EditorContextKeys.inMultiDiffEditor,
            menu: {
                when: EditorContextKeys.inMultiDiffEditor,
                id: MenuId.MultiDiffEditorFileToolbar,
                order: 22,
                group: 'navigation',
            },
        });
    }
    async run(accessor, ...args) {
        const uri = args[0];
        const editorService = accessor.get(IEditorService);
        const activeEditorPane = editorService.activeEditorPane;
        let selections = undefined;
        if (activeEditorPane instanceof MultiDiffEditor) {
            const editor = activeEditorPane.tryGetCodeEditor(uri);
            if (editor) {
                selections = editor.editor.getSelections() ?? undefined;
            }
        }
        const editor = await editorService.openEditor({ resource: uri });
        if (selections && (editor instanceof TextFileEditor)) {
            const c = editor.getControl();
            if (c) {
                c.setSelections(selections);
                c.revealLineInCenter(selections[0].selectionStartLineNumber);
            }
        }
    }
}
class CollapseAllAction extends Action2 {
    constructor() {
        super({
            id: 'multiDiffEditor.collapseAll',
            title: ( localize2WithPath(_moduleId, 1, 'Collapse All Diffs')),
            icon: Codicon.collapseAll,
            precondition: ( (ContextKeyExpr.and(
                 (ContextKeyExpr.equals('activeEditor', MultiDiffEditor.ID)),
                ContextKeyExpr.not('multiDiffEditorAllCollapsed')
            ))),
            menu: {
                when: ( (ContextKeyExpr.and(
                     (ContextKeyExpr.equals('activeEditor', MultiDiffEditor.ID)),
                    ContextKeyExpr.not('multiDiffEditorAllCollapsed')
                ))),
                id: MenuId.EditorTitle,
                group: 'navigation',
                order: 100
            },
            f1: true,
        });
    }
    async run(accessor, ...args) {
        const editorService = accessor.get(IEditorService);
        const activeEditor = editorService.activeEditor;
        if (activeEditor instanceof MultiDiffEditorInput) {
            const viewModel = await activeEditor.getViewModel();
            viewModel.collapseAll();
        }
    }
}
class ExpandAllAction extends Action2 {
    constructor() {
        super({
            id: 'multiDiffEditor.expandAll',
            title: ( localize2WithPath(_moduleId, 2, 'Expand All Diffs')),
            icon: Codicon.expandAll,
            precondition: ( (ContextKeyExpr.and(
                 (ContextKeyExpr.equals('activeEditor', MultiDiffEditor.ID)),
                 (ContextKeyExpr.has('multiDiffEditorAllCollapsed'))
            ))),
            menu: {
                when: ( (ContextKeyExpr.and(
                     (ContextKeyExpr.equals('activeEditor', MultiDiffEditor.ID)),
                     (ContextKeyExpr.has('multiDiffEditorAllCollapsed'))
                ))),
                id: MenuId.EditorTitle,
                group: 'navigation',
                order: 100
            },
            f1: true,
        });
    }
    async run(accessor, ...args) {
        const editorService = accessor.get(IEditorService);
        const activeEditor = editorService.activeEditor;
        if (activeEditor instanceof MultiDiffEditorInput) {
            const viewModel = await activeEditor.getViewModel();
            viewModel.expandAll();
        }
    }
}

export { CollapseAllAction, ExpandAllAction, GoToFileAction };
